/*
 * Walk the object tree and return a list of field diff descriptors for leaves.
 *
 * Diffs from right to left, so obj1 is typically the "old" object and obj2 is the "new" object.
 */

export type FieldDiffDesc = {
  path: string[];
  value1: unknown;
  value2: unknown;
};

type DiffableObject = Record<string, unknown> | Array<unknown>;

type DeepDiffObjectParams = {
  obj1: DiffableObject;
  obj2: DiffableObject;
  excludePaths: string[];
  basePath?: string[];
  /** Key used to match array elements. Defaults to "id". Set to null for index-based matching. */
  arrayMatchKey?: string | null;
};

/**
 * Checks deep equality of two values using JSON serialization.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object" || a === null || b === null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Gets a match key from an array element.
 * Returns the value of the matchKey property if it exists, otherwise returns a JSON string for value matching.
 */
function getElementKey(element: unknown, matchKey: string | null): string {
  if (element === null) return "value:null";
  if (element === undefined) return "value:undefined";

  if (
    matchKey !== null &&
    typeof element === "object" &&
    matchKey in (element as Record<string, unknown>)
  ) {
    const keyValue = (element as Record<string, unknown>)[matchKey];
    if (typeof keyValue === "string" || typeof keyValue === "number") {
      return `key:${keyValue}`;
    }
  }

  return `value:${JSON.stringify(element)}`;
}

/**
 * Diffs two arrays by matching elements using a key (default "id") or by value equality.
 */
function diffArrays({
  arr1,
  arr2,
  excludePaths,
  basePath,
  arrayMatchKey,
}: {
  arr1: unknown[];
  arr2: unknown[];
  excludePaths: string[];
  basePath: string[];
  arrayMatchKey: string | null;
}): FieldDiffDesc[] {
  const diffs: FieldDiffDesc[] = [];

  const arr1ByKey = new Map<string, { element: unknown; index: number }>();
  const arr2ByKey = new Map<string, { element: unknown; index: number }>();

  arr1.forEach((element, index) => {
    arr1ByKey.set(getElementKey(element, arrayMatchKey), { element, index });
  });

  arr2.forEach((element, index) => {
    arr2ByKey.set(getElementKey(element, arrayMatchKey), { element, index });
  });

  // Additions and modifications
  for (const [key, { element: elem2, index: idx2 }] of arr2ByKey) {
    const fieldPath = [...basePath, String(idx2)];
    if (excludePaths.includes(fieldPath.join("."))) continue;

    const arr1Entry = arr1ByKey.get(key);

    if (!arr1Entry) {
      diffs.push({ path: fieldPath, value1: undefined, value2: elem2 });
    } else {
      const elem1 = arr1Entry.element;

      if (
        typeof elem1 === "object" &&
        elem1 !== null &&
        typeof elem2 === "object" &&
        elem2 !== null &&
        !Array.isArray(elem1) &&
        !Array.isArray(elem2)
      ) {
        diffs.push(
          ...deepDiffObjects({
            obj1: elem1 as DiffableObject,
            obj2: elem2 as DiffableObject,
            excludePaths,
            basePath: fieldPath,
            arrayMatchKey,
          }),
        );
      } else if (!deepEqual(elem1, elem2)) {
        diffs.push({ path: fieldPath, value1: elem1, value2: elem2 });
      }
    }
  }

  // Deletions
  for (const [key, { element: elem1, index: idx1 }] of arr1ByKey) {
    if (!arr2ByKey.has(key)) {
      const fieldPath = [...basePath, String(idx1)];
      if (!excludePaths.includes(fieldPath.join("."))) {
        diffs.push({ path: fieldPath, value1: elem1, value2: undefined });
      }
    }
  }

  return diffs;
}

export function deepDiffObjects({
  obj1,
  obj2,
  excludePaths = [],
  basePath = [],
  arrayMatchKey = "id",
}: DeepDiffObjectParams): FieldDiffDesc[] {
  const diffs: FieldDiffDesc[] = [];

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    return diffArrays({
      arr1: obj1,
      arr2: obj2,
      excludePaths,
      basePath,
      arrayMatchKey,
    });
  }

  for (const key in obj2) {
    const fieldPath = [...basePath, key];
    if (excludePaths.includes(fieldPath.join("."))) continue;

    const obj1Value = obj1[key];
    const obj2Value = obj2[key];

    if (
      (obj1Value === undefined || obj2Value === undefined) &&
      obj1Value !== obj2Value
    ) {
      diffs.push({ path: fieldPath, value1: obj1Value, value2: obj2Value });
      continue;
    }

    if (Array.isArray(obj1Value) && Array.isArray(obj2Value)) {
      diffs.push(
        ...diffArrays({
          arr1: obj1Value,
          arr2: obj2Value,
          excludePaths,
          basePath: fieldPath,
          arrayMatchKey,
        }),
      );
      continue;
    }

    if (
      typeof obj1Value === "object" &&
      obj1Value !== null &&
      typeof obj2Value === "object" &&
      obj2Value !== null
    ) {
      diffs.push(
        ...deepDiffObjects({
          obj1: obj1Value as DiffableObject,
          obj2: obj2Value as DiffableObject,
          excludePaths,
          basePath: fieldPath,
          arrayMatchKey,
        }),
      );
      continue;
    }

    if (obj1Value !== obj2Value) {
      diffs.push({ path: fieldPath, value1: obj1Value, value2: obj2Value });
    }
  }

  return diffs;
}
